import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Small hook that mirrors a useState signature but reads/writes to the URL
// query string. Default values are omitted from the URL so canonical paths
// stay clean. Text inputs can pass debounceMs so we don't push a history
// entry per keystroke.
//
// The goal is shareable links: copying /platform/tenants?status=active&q=smith
// should land anyone on the exact same filtered view.

export function useUrlState<T extends string>(
  key: string,
  defaultValue: T,
  debounceMs?: number,
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();

  const raw = params.get(key);
  // Read authoritatively from URL — source of truth. Falls back to default
  // when the URL value is missing or not a recognised string.
  const current = (raw ?? defaultValue) as T;

  // Optimistic local state for debounced writes: users see instant input
  // feedback even while we delay the URL commit.
  const [optimistic, setOptimistic] = useState<T>(current);
  useEffect(() => {
    setOptimistic(current);
  }, [current]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const commit = useCallback(
    (next: T) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === defaultValue || next === "") {
            out.delete(key);
          } else {
            out.set(key, String(next));
          }
          return out;
        },
        { replace: true },
      );
    },
    [defaultValue, key, setParams],
  );

  const set = useCallback(
    (next: T) => {
      setOptimistic(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (debounceMs && debounceMs > 0) {
        timerRef.current = setTimeout(() => commit(next), debounceMs);
      } else {
        commit(next);
      }
    },
    [commit, debounceMs],
  );

  return [debounceMs ? optimistic : current, set];
}

// Convenience for booleans encoded as "1" / absent.
export function useUrlBool(key: string, defaultValue = false): [boolean, (next: boolean) => void] {
  const [value, setValue] = useUrlState<"1" | "">(key, defaultValue ? "1" : "");
  return [value === "1", (next: boolean) => setValue(next ? "1" : "")];
}

// Session-sticky query string.
//
// Filters here live in the URL (shareable, and a plain refresh keeps them
// because the URL itself is what reloads). What the URL can't survive is
// LEAVING the page: clicking the sidebar link back to a filtered list lands on
// a bare path, so the cohort a registrar had narrowed to is silently lost.
//
// This mirrors the page's query string into sessionStorage and restores it
// when the page is opened with no query of its own. Session (not local)
// storage on purpose: it is per-tab and dies with the browser session, so one
// tab's filters never leak into another, and a filtered view is never
// resurrected days later for someone who has forgotten they set it.
//
// Clearing every filter persists the EMPTY query, so "Clear filters" stays
// cleared across a refresh instead of springing back.
export function useStickyQuery(scope: string): void {
  const [params, setParams] = useSearchParams();
  const storageKey = `exambase:filters:${scope}`;
  // Restore at most once per mount, and never when the caller arrived with an
  // explicit query (a shared link must win over whatever this tab last used).
  const restored = useRef(false);

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (params.toString() !== "") return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(storageKey);
    } catch {
      // Private-mode / storage-disabled: stickiness is a convenience, so a
      // failure here degrades to the plain URL behaviour.
      return;
    }
    if (saved) setParams(new URLSearchParams(saved), { replace: true });
  }, [params, setParams, storageKey]);

  const current = params.toString();
  useEffect(() => {
    // Don't overwrite a saved query with the empty string on the very first
    // render, before the restore effect above has had its chance to run.
    if (!restored.current) return;
    try {
      sessionStorage.setItem(storageKey, current);
    } catch {
      /* storage unavailable — nothing to persist to */
    }
  }, [current, storageKey]);
}
